import { DevBanner } from '@bestseller/retail-planning.ui.atoms.dev-banner';
import {
    ApplicationShell,
    ApplicationShellFilterProps,
    Environment,
} from '@bestseller-bit/frontend-community.bootstrapping.application-shell';
import { Box, Stack } from '@mui/system';
import type { JSX } from 'react';
import { FC, PropsWithChildren, useCallback, useMemo } from 'react';
import { Route as ReactRoute, Routes, useLocation, useNavigate } from 'react-router-dom';
import { PipelineStatus } from './components/molecules/PipelineStatus/PipelineStatus';
import { StatusWidget } from './components/molecules/StatusWidget/StatusWidget';
import { DevTools } from './components/organisms/DevTools/DevTools';
import { VersionLog } from './components/organisms/VersionLog/VersionLog';
import { AnnouncementSystem } from './components/views/AnnouncementSystem/AnnouncementSystem';
import { Frontpage } from './components/views/Frontpage/Frontpage';
import { NoAccess } from './components/views/NoAccess/NoAccess';
import { useDynamicTitle } from './hooks/useDynamicTitle';
import { useFilterCategories } from './hooks/useFilterCategories';
import { useFiltersState } from './hooks/useFilterState';
import { useHasDevTools } from './hooks/useHasDevTools';
import { useIsDevEnv } from './hooks/useIsDevEnv';
import { useMenuItems } from './hooks/useMenuItems';
import { usePinFilters } from './hooks/usePinFilters';
import { useShouldShowFilterDesktop } from './hooks/useShouldShowFilters';
import { useSyncFilters } from './hooks/useSyncFilters';
import { useRoutes } from './useRoutes';

export const Router = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const routes = useRoutes();
    const hasDevTools = useHasDevTools();
    const filterCategories = useFilterCategories();
    const shouldShowFilters = useShouldShowFilterDesktop();
    const [filtersState, setFiltersState] = useFiltersState();
    const [pinned, handlePinChange] = usePinFilters();

    const isDevEnv = useIsDevEnv();

    // update title when a new module is visited
    useDynamicTitle();

    // Handle filter sync
    const { clearFilters } = useSyncFilters({ filtersState, setFiltersState });

    const onNavigate = useCallback(
        (menuUrl: string, openNewTab: boolean) => {
            if (openNewTab) {
                globalThis.open(menuUrl, '_blank');
                return;
            }

            // Left menu unfortunately prefixes all URLs with a slash.. So check for front page needs to check double slash
            if (menuUrl === '//') {
                // If the menu URL is the front page, navigate to the root path
                navigate({
                    pathname: '/',
                });
                return;
            }

            navigate({
                pathname: menuUrl,
                search: location.search,
            });
        },
        [location.search, navigate]
    );

    const menuItems = useMenuItems();

    const actionComponents = useMemo(() => {
        return [
            isDevEnv ? (
                <Box key="dev-banner" sx={{ height: 32, alignSelf: 'center', display: 'flex', alignItems: 'center' }}>
                    <DevBanner />
                </Box>
            ) : null,
            <PipelineStatus key="pipeline-status" />,
            <VersionLog key="version-log" />,
            hasDevTools ? <DevTools key="dev-tools" /> : null,
            <StatusWidget key="status-widget" />,
        ];
    }, [hasDevTools, isDevEnv]);

    const filterProps: ApplicationShellFilterProps | undefined = useMemo(() => {
        if (shouldShowFilters && filtersState && setFiltersState) {
            return {
                categories: filterCategories,
                filtersState: [filtersState, setFiltersState],
                isPinned: pinned,
                collapsable: pinned,
                onPinCallback: handlePinChange,
                onClearCallback: clearFilters,
            };
        }
    }, [shouldShowFilters, filtersState, setFiltersState, filterCategories, clearFilters, pinned, handlePinChange]);

    const env: Environment = import.meta.env.MODE === 'production' ? 'prod' : 'dev';

    return (
        <ApplicationShell
            menuProps={{
                menuItems,
                navigate: onNavigate,
            }}
            topbarProps={{
                applicationName: 'Storeforecast',
                env,
                actionComponents,
                appSwitcherProps: {
                    excludeApps: ['Storeforecast'],
                },
            }}
            filterProps={filterProps}
        >
            <Stack
                sx={{
                    height: '100%',
                }}
            >
                <Stack
                    direction="column"
                    sx={{
                        flexGrow: 1,
                        alignItems: 'stretch',
                    }}
                >
                    <Routes>
                        <ReactRoute path={`/`} element={<Frontpage />} />
                        <ReactRoute path={`/status`} element={<AnnouncementSystem />} />
                        <ReactRoute path={`/no-access`} element={<NoAccess />} />

                        {routes.map((route) => (
                            <ReactRoute
                                key={route.path}
                                path={route.path}
                                // Previous solution was reported as a codesmell in sonar cloud, this solution is not and is inspired from here: https://stackoverflow.com/questions/50888615/dynamically-nested-react-components#answer-50889391
                                element={
                                    <RouteWithGuards guards={[...(route.guards ?? [])]} component={route.component} />
                                }
                            />
                        ))}
                    </Routes>
                </Stack>
            </Stack>
        </ApplicationShell>
    );
};

interface RouteWithGuardsProps {
    guards: Array<FC<PropsWithChildren<unknown>>>;
    component: JSX.Element;
}
const RouteWithGuards: FC<RouteWithGuardsProps> = ({ guards, component }) => {
    const Guard = useMemo(() => guards[0], [guards]);

    if (guards.length > 0) {
        return (
            <Guard>
                <RouteWithGuards guards={guards.slice(1)} component={component}></RouteWithGuards>
            </Guard>
        );
    }
    return component;
};
